import type {
  CheckResult,
  CheckStats,
  CheckStatus,
  HubUser,
  Incident,
  MetricSeriesResponse,
  MonthlyReportResponse,
  QuietWindow,
  SiteInventory,
  StatusCounts,
} from "@central-hub/contracts";
import { type PointStatus, pointStatus } from "./chart-math";
import { ago, capList, DAY_MS, duration, formatBytes, plural } from "./format";

/** Checks the hub reports about itself; shown apart from the targets it watches. */
export const HUB_TARGET = "central-hub";

/**
 * Checks about a target's backups rather than its availability: shown in their own panel, kept
 * out of the target's card, overview and uptime so a failed backup never reads as an outage.
 */
export const BACKUP_CHECKS: ReadonlySet<string> = new Set(["backup", "restore"]);

/**
 * The errors a target's logs show (kubernetes `logs`): shown in the errors view only, kept out
 * of the wall, its counts and incidents, so a noisy log never reads as an outage.
 */
export const LOG_CHECK = "logs";
/** The apperrors module's check: errors an application reported itself. */
export const APP_ERRORS_CHECK = "app-errors";

/** Web security and dependency checks (`security:web`, `security:dependencies`). */
export const SECURITY_PREFIX = "security:";
/** Prefix of stock watches (hardware that sells out), shown in the Servers view. */
export const STOCK_PREFIX = "stock:";

/** Checks about the cluster as a whole (nodes, storage, certificates, stuck pods). */
export const CLUSTER_TARGET = "cluster";

/** Latest deploys listed in the rail; the deploys view lists them all. */
export const RAIL_DEPLOYS = 8;
/** Targets above this count switch the "auto" density to compact tiles. */
export const AUTO_COMPACT_TARGETS = 12;
/** Targets named in the hero band before "+N more". */
const HERO_NAMES = 2;
/** Healthy certificates listed after the ones needing attention. */
const HEALTHY_CERTS_SHOWN = 6;

const RANK: Record<CheckStatus, number> = { up: 0, unknown: 1, degraded: 2, down: 3 };

export const STATUS_LABEL: Record<CheckStatus, string> = {
  up: "Up",
  degraded: "Degraded",
  down: "Down",
  unknown: "Unknown",
};

export type Density = "auto" | "comfortable" | "compact";

export interface CheckRow {
  name: string;
  status: CheckStatus;
  latency: string;
  message: string;
  bad: boolean;
  /** 24 hourly latency averages for the sparkline (empty when unknown). */
  spark: (number | null)[];
  uptime24h: number | null;
  uptime30d: number | null;
  /** Where the check's subject lives (a CI run), when the module gave a web link. */
  link?: string;
}

export interface TargetCard {
  name: string;
  status: CheckStatus;
  problem: boolean;
  checks: CheckRow[];
  /** Compact tile subtitle: failing checks, or all check names when healthy. */
  summary: string;
  upRatio: string;
  certProblem?: { check: string; status: CheckStatus; text: string };
  /** Worst 30-day availability among its checks: the honest figure for the whole site. */
  uptime30d: number | null;
  /** Every certificate check of the target, for the detail view. */
  certificates: { check: string; status: CheckStatus; text: string }[];
  /** Its backup and restore-test checks, for the detail view. */
  backups: { check: string; status: CheckStatus; text: string }[];
  /** Which build its pods run and whether the watched branch is ahead, when known. */
  version?: DeployedVersion;
  /** Its deploy workflow's check, and the run going on now, if any. */
  deploy?: { check: string; running?: { since: number; url?: string } };
  /** Its pods' CPU (cores) and memory (bytes) limits, when they have them. */
  limits?: { cpu?: number; memory?: number };
  /** Its alerts are held: a maintenance window (or a deploy) — why, and until when. */
  quiet?: QuietWindow;
  /** Everything known about the site, for its sheet. */
  sheet: SiteSheet;
}

export interface SiteSheet {
  /** "https://shop.example.com", one per host its checks visit. */
  addresses: string[];
  repo?: { name: string; url?: string; branch?: string };
  /** "namespace/deployment". */
  deployment?: string;
  /** The image tag its pods run. */
  running?: string;
  volumes: { name: string; size: string }[];
  backup?: { when: string; size: string };
  /** The certificate that expires first. */
  certificate?: string;
  inventory?: SiteInventory;
}

/** One finished deploy, for the detail view's list. */
export interface DeployRow {
  key: string;
  status: CheckStatus;
  conclusion: string;
  when: string;
  commit: string;
  actor: string;
  duration: string;
  url?: string;
  /** Epoch ms the run finished, when known. */
  finishedAt?: number;
}

export interface DeployedVersion {
  /** Image tag(s) the target's pods run ("1a2b3c4d5e6f-7", "latest"). */
  running: string;
  /** The commit that build came from, when its tag names one (`<commit>-<run>`). */
  commit?: string;
  /** The watched branch's latest commit and whether it is the one running. */
  branch?: { head: string; upToDate: boolean; compare?: string };
  /** The deploy workflow's page on GitHub, where it is run (the hub never deploys). */
  deployUrl?: string;
  /** Earlier builds the Deployment kept, newest first, with the command that puts one back. */
  rollback?: { tag: string; command: string }[];
}

export interface BackupCell {
  status: CheckStatus;
  /** Short: "3 h ago · 256 MB" when fine, the status otherwise. */
  text: string;
  message: string;
}

export interface BackupRow {
  target: string;
  /** Worst of its backup and restore checks. */
  status: CheckStatus;
  bad: boolean;
  backup: BackupCell | null;
  restore: BackupCell | null;
}

export interface OpenIncidentRow {
  id: number;
  target: string;
  check: string;
  status: CheckStatus;
  message: string;
  /** Epoch ms; drives the live "ongoing for" counter. */
  openedAt: number;
}

export interface ResolvedRow {
  id: number;
  target: string;
  check: string;
  status: CheckStatus;
  text: string;
  resolvedAt: number;
  message: string;
}

export interface CertRow {
  target: string;
  check: string;
  status: CheckStatus;
  /** Whole days left; negative once expired; null when unknown. */
  days: number | null;
  expiresOn: string | null;
  bad: boolean;
  message: string;
}

export type HeroTone = "ok" | "degraded" | "down" | "unknown" | "empty";

export interface Hero {
  tone: HeroTone;
  title: string;
  detail: string;
  /** Epoch ms of the longest ongoing outage, rendered live. */
  longestSince?: number;
}

export type OverviewRange = "24h" | "30d";

export interface OverviewSlot {
  status: PointStatus;
  counts: StatusCounts;
}

export interface OverviewRow {
  name: string;
  /** Current status, for the row's icon. */
  status: CheckStatus;
  /** Worst availability among the site's checks over the range (same figure as the card). */
  uptime: number | null;
  slots: OverviewSlot[];
  /** Slots with at least one failed result. */
  failingSlots: number;
}

export interface Overview {
  range: OverviewRange;
  /** Start of each slot, epoch ms, oldest first. */
  slotStarts: number[];
  slotMs: number;
  rows: OverviewRow[];
}

export interface ClusterNodeRow {
  /** Short name used in check ids ("worker-1"); the node's own name when it has no alias. */
  alias: string;
  node: string;
  status: CheckStatus;
  message: string;
  /** Usage ratios 0–1, null when metrics are unavailable. */
  cpu: number | null;
  memory: number | null;
  pods: string;
  /** Targets with pods on this node. */
  targets: string[];
  /** Last 24 h of usage ratios for the sparklines; empty until loaded. */
  cpuSpark: (number | null)[];
  memorySpark: (number | null)[];
}

export interface ClusterPart {
  status: CheckStatus;
  text: string;
}

export interface ClusterView {
  status: CheckStatus;
  nodes: ClusterNodeRow[];
  storage: (ClusterPart & { ratio: number | null }) | null;
  certificates: ClusterPart | null;
  pods: ClusterPart | null;
}

/** 24 h of a usage ratio per node alias, from `/api/v1/metrics/series`. */
export interface NodeUsage {
  cpu: Record<string, (number | null)[]>;
  memory: Record<string, (number | null)[]>;
}

export interface ViewModel {
  empty: boolean;
  /** Every site's status over time, at both resolutions; null until stats arrive. */
  overview: Record<OverviewRange, Overview> | null;
  compact: boolean;
  counts: { status: CheckStatus; n: number }[];
  hero: Hero;
  hub: { total: number; failing: { check: string; status: CheckStatus; message: string }[] };
  open: OpenIncidentRow[];
  cards: TargetCard[];
  targetsMeta: string;
  certs: { shown: CertRow[]; more: string; summary: string; needAttention: number };
  backups: { rows: BackupRow[]; summary: string; needAttention: number };
  resolved: ResolvedRow[];
  /** Nodes, storage and certificates; null when the hub doesn't watch a cluster. */
  cluster: ClusterView | null;
  /** Deploys going on and the latest ones, across targets; null when none has a deploy workflow. */
  deploys: DeployFeed | null;
  /** The deploys view; null when no target has a deploy workflow. */
  deployBoard: DeployBoard | null;
  /** The errors view; null when the hub doesn't read logs. */
  errorBoard: ErrorBoard | null;
  /** Errors the applications report themselves; empty when none does. */
  appErrors: AppErrorSite[];
  /** The security view; null when nothing is scanned. */
  securityBoard: SecurityBoard | null;
  serverBoard: ServerBoard | null;
}

/** One target in the deploys view: its build, its last deploy and how its deploys have gone. */
export interface DeploySite {
  target: string;
  /** Its deploy workflow's last finished run. */
  last?: DeployRow;
  running?: { since: number; url?: string };
  version?: DeployedVersion;
  /** Its deploy workflow's page on GitHub, where it is run. */
  deployUrl?: string;
  /** Deploys of it in `history`, and how many of those failed. */
  count: number;
  failed: number;
}

export interface DeployBoard {
  /** In progress first, then a failed last deploy, then undeployed commits, then latest deploy first. */
  sites: DeploySite[];
  running: number;
  upToDate: number;
  /** Targets whose branch has commits their pods don't run yet. */
  behind: string[];
  /** Targets whose last deploy failed. */
  failed: string[];
  /** Targets running an image whose tag names no commit ("latest"): no telling what runs. */
  untraceable: string[];
  /** Every target's recent deploys, newest first (what `/api/v1/runs` returned). */
  history: (DeployRow & { target: string })[];
  historyFailed: number;
}

export interface ErrorMessage {
  text: string;
  count: number;
  severity: "fatal" | "error" | "warning";
  /** Epoch ms it was last logged in the window, when the log had timestamps. */
  last?: number;
}

/** One target in the errors view: what its last log window showed. */
export interface ErrorSite {
  target: string;
  status: CheckStatus;
  message: string;
  /** Answers through the ingress; null when the ingress isn't read. */
  requests: number | null;
  http5xx: number | null;
  fatal: number;
  error: number;
  warning: number;
  top: ErrorMessage[];
  /** The last 24 hours, oldest first: whether each hour had errors (degraded) or not. */
  hours: PointStatus[];
}

export interface ErrorBoard {
  /** Problems first (fatal errors, 5xx, errors, warnings), then by name. */
  sites: ErrorSite[];
  windowMinutes: number;
  /** Epoch ms of the latest read: the window is the `windowMinutes` before it. */
  readAt: number | null;
  withFatal: string[];
  with5xx: string[];
  withWarnings: number;
  requests: number;
  http5xx: number;
  /** Targets whose logs couldn't be read. */
  unread: string[];
}

export interface AppErrorSite {
  target: string;
  status: CheckStatus;
  message: string;
  issues: number;
  newIssues24h: number;
  events24h: number;
  errors24h: number;
  warnings24h: number;
  /** Epoch ms of its latest event; null when nothing was reported in the window. */
  lastAt: number | null;
  windowDays: number;
}

export interface DeployFeed {
  running: { target: string; since: number; url?: string }[];
  /** Latest first; empty until loaded. */
  recent: (DeployRow & { target: string })[];
}

export interface ModelInput {
  checks: readonly CheckResult[];
  stats?: readonly CheckStats[];
  open: readonly Incident[];
  resolved: readonly Incident[];
  now: number;
  density: Density;
  usage?: NodeUsage;
  /** Recent deploys of every target (`/api/v1/runs?having=deployUrl`), newest first. */
  deploys?: readonly { target: string; data: CheckResult["data"] }[];
  /** Targets whose alerts are held now (`/api/v1/maintenance` active). */
  quiet?: readonly QuietWindow[];
  /** What the config says about each site (`/api/v1/inventory`). */
  inventory?: Readonly<Record<string, SiteInventory>>;
}

const worstOf = (checks: readonly CheckResult[]): CheckStatus =>
  checks.reduce<CheckStatus>((w, c) => (RANK[c.status] > RANK[w] ? c.status : w), "up");

const isTls = (check: string) => check.startsWith("tls:");

/** Only http(s) links are rendered: data comes from modules, and a `javascript:` URL must never become a link. */
export function webLink(value: unknown): string | undefined {
  return typeof value === "string" && /^https?:\/\//.test(value) ? value : undefined;
}
const isBackup = (c: Pick<CheckResult, "check">) => BACKUP_CHECKS.has(c.check);
/** Side checks: shown in their own views (Errors, Security, Servers), never on the wall. */
const isLogs = (c: Pick<CheckResult, "check">) =>
  c.check === LOG_CHECK ||
  c.check === APP_ERRORS_CHECK ||
  c.check.startsWith(SECURITY_PREFIX) ||
  c.check.startsWith(STOCK_PREFIX);

/** "registry.example.com/acme/shop:1a2b-7" → "1a2b-7"; a digest-only reference → "sha256:1a2b3c4". */
function imageTag(image: string): string {
  const [name = "", digest] = image.split("@");
  const tag = name.slice(name.lastIndexOf("/") + 1).split(":")[1];
  if (tag) return tag;
  return digest ? digest.slice(0, 14) : "latest";
}

/**
 * Joins what the cluster runs (kubernetes:pods reports the image, comma-separated mid-rollout) with the branch head the CI
 * checks report: running `<commit>-<run>` while the branch is at another commit means changes
 * that haven't been deployed.
 */
export function deployedVersion(checks: readonly CheckResult[]): DeployedVersion | undefined {
  const image = checks.find((c) => c.check === "kubernetes:pods")?.data?.image;
  if (typeof image !== "string" || image === "") return undefined;
  const tags = [...new Set(image.split(", ").map(imageTag))];
  const running = tags.join(", ");
  const extras = deployExtras(checks);
  const commit =
    tags.length === 1 ? /^([0-9a-f]{7,40})(?:-\d+)?$/.exec(tags[0] ?? "")?.[1] : undefined;
  if (!commit) return { running, ...extras };

  const heads = checks.flatMap((c) => {
    const head = c.data?.headSha;
    const repo = c.data?.repo;
    return c.check.startsWith("ci:") && typeof head === "string" && typeof repo === "string"
      ? [{ head, repo, url: c.data?.url }]
      : [];
  });
  const current = heads.find((h) => h.head.startsWith(commit));
  if (current) {
    return { running, commit, branch: { head: current.head, upToDate: true }, ...extras };
  }
  // Only one repository can be the one that was built; with several, there is no telling which.
  const [only, ...others] = heads;
  if (!only || others.some((h) => h.repo !== only.repo)) return { running, commit, ...extras };
  const base = typeof only.url === "string" ? only.url.split("/actions/")[0] : undefined;
  const compare = webLink(base && `${base}/compare/${commit}...${only.head}`);
  return {
    running,
    commit,
    branch: { head: only.head, upToDate: false, ...(compare && { compare }) },
    ...extras,
  };
}

/** The check of the target's deploy workflow (the one the github module links), and its run now. */
export function deployOf(checks: readonly CheckResult[]): TargetCard["deploy"] {
  const check = checks.find((c) => webLink(c.data?.deployUrl));
  if (!check) return undefined;
  const since = timeOf(check.data?.runningSince);
  const url = webLink(check.data?.runningUrl);
  return {
    check: check.check,
    ...(!Number.isNaN(since) && { running: { since, ...(url && { url }) } }),
  };
}

const RUN_STATUS: Record<string, CheckStatus> = {
  success: "up",
  failure: "down",
  timed_out: "down",
  startup_failure: "down",
};

/** The runs `/api/v1/runs` returned for a deploy check, as list rows (newest first). */
export function deployRows(runs: readonly CheckResult["data"][], now: number): DeployRow[] {
  return runs.flatMap((run) => {
    const url = webLink(run?.url);
    if (!run || !url) return [];
    const finished = timeOf(run.finishedAt);
    const started = timeOf(run.startedAt);
    const conclusion = typeof run.conclusion === "string" ? run.conclusion : "unknown";
    return [
      {
        key: url,
        status: RUN_STATUS[conclusion] ?? "unknown",
        conclusion,
        when: Number.isNaN(finished) ? "—" : ago(now - finished),
        commit: typeof run.sha === "string" ? run.sha.slice(0, 7) : "—",
        actor: typeof run.actor === "string" ? run.actor : "",
        duration:
          Number.isNaN(finished) || Number.isNaN(started) ? "" : duration(finished - started),
        url,
        ...(!Number.isNaN(finished) && { finishedAt: finished }),
      },
    ];
  });
}

const SEVERITIES = new Set(["fatal", "error", "warning"]);
const ERROR_ORDER = (s: ErrorSite) =>
  s.status === "unknown"
    ? 5
    : s.fatal > 0
      ? 0
      : (s.http5xx ?? 0) > 0
        ? 1
        : s.error > 0
          ? 2
          : s.warning > 0
            ? 3
            : 4;

/** The errors view, from every target's `logs` check and its hourly history. */
export function buildErrorBoard(
  checks: readonly CheckResult[],
  statOf: (c: CheckResult) => CheckStats | undefined,
): ErrorBoard | null {
  if (checks.length === 0) return null;
  const count = (v: unknown) => (typeof v === "number" ? v : 0);
  const sites = checks.map((c): ErrorSite => {
    const d = c.data ?? {};
    const top = [1, 2, 3].flatMap((i): ErrorMessage[] => {
      const text = d[`top${i}`];
      const severity = d[`top${i}Severity`];
      const last = timeOf(d[`top${i}Last`]);
      return typeof text === "string" && typeof severity === "string" && SEVERITIES.has(severity)
        ? [
            {
              text,
              count: count(d[`top${i}Count`]),
              severity: severity as ErrorMessage["severity"],
              ...(!Number.isNaN(last) && { last }),
            },
          ]
        : [];
    });
    return {
      target: c.target,
      status: c.status,
      message: c.message ?? "",
      requests: typeof d.requests === "number" ? d.requests : null,
      http5xx: typeof d.http5xx === "number" ? d.http5xx : null,
      fatal: count(d.fatal),
      error: count(d.error),
      warning: count(d.warning),
      top,
      hours: (statOf(c)?.counts24h ?? []).map(pointStatus),
    };
  });
  sites.sort(
    (a, b) =>
      ERROR_ORDER(a) - ERROR_ORDER(b) ||
      b.fatal - a.fatal ||
      (b.http5xx ?? 0) - (a.http5xx ?? 0) ||
      b.error + b.warning - (a.error + a.warning) ||
      a.target.localeCompare(b.target),
  );
  const window = checks.map((c) => c.data?.windowSeconds).find((w) => typeof w === "number");
  const reads = checks.map((c) => Date.parse(c.observedAt)).filter((t) => !Number.isNaN(t));
  return {
    sites,
    windowMinutes: typeof window === "number" ? Math.round(window / 60) : 5,
    readAt: reads.length > 0 ? Math.max(...reads) : null,
    withFatal: sites.filter((s) => s.fatal > 0).map((s) => s.target),
    with5xx: sites.filter((s) => (s.http5xx ?? 0) > 0).map((s) => s.target),
    withWarnings: sites.filter((s) => s.warning > 0 || s.error > 0).length,
    requests: sites.reduce((n, s) => n + (s.requests ?? 0), 0),
    http5xx: sites.reduce((n, s) => n + (s.http5xx ?? 0), 0),
    unread: sites.filter((s) => s.status === "unknown").map((s) => s.target),
  };
}

/** Sites to flag on the Errors tab: fatal errors in their logs, or their application reporting errors now. */
export function erroringSites(vm: Pick<ViewModel, "errorBoard" | "appErrors">): number {
  const names = new Set(vm.errorBoard?.withFatal ?? []);
  for (const site of vm.appErrors) if (site.status === "degraded") names.add(site.target);
  return names.size;
}

/** The applications' own errors: erroring now first, then the most recent. */
export function buildAppErrors(checks: readonly CheckResult[]): AppErrorSite[] {
  const count = (v: unknown) => (typeof v === "number" ? v : 0);
  return checks
    .map((c): AppErrorSite => {
      const d = c.data ?? {};
      const last = timeOf(d.lastAt);
      return {
        target: c.target,
        status: c.status,
        message: c.message ?? "",
        issues: count(d.issues),
        newIssues24h: count(d.newIssues24h),
        events24h: count(d.events24h),
        errors24h: count(d.errors24h),
        warnings24h: count(d.warnings24h),
        lastAt: Number.isNaN(last) ? null : last,
        windowDays: count(d.windowDays) || 7,
      };
    })
    .sort(
      (a, b) =>
        Number(b.status === "degraded" || b.status === "down") -
          Number(a.status === "degraded" || a.status === "down") ||
        (b.lastAt ?? 0) - (a.lastAt ?? 0) ||
        a.target.localeCompare(b.target),
    );
}

const DEPLOY_ORDER = (site: DeploySite) =>
  site.running
    ? 0
    : site.last?.status === "down"
      ? 1
      : site.version?.branch?.upToDate === false
        ? 2
        : 3;

/** The deploys view: every target with a deploy workflow, and the fleet's recent deploys. */
export function buildDeployBoard(
  cards: readonly TargetCard[],
  checks: readonly CheckResult[],
  history: readonly (DeployRow & { target: string })[],
  now: number,
): DeployBoard | null {
  const sites = cards.flatMap((card): DeploySite[] => {
    if (!card.deploy) return [];
    const check = checks.find((c) => c.target === card.name && c.check === card.deploy?.check);
    const [last] = check ? deployRows([check.data], now) : [];
    const own = history.filter((h) => h.target === card.name);
    const deployUrl = webLink(check?.data?.deployUrl);
    return [
      {
        target: card.name,
        ...(last && { last }),
        ...(card.deploy.running && { running: card.deploy.running }),
        ...(card.version && { version: card.version }),
        ...(deployUrl && { deployUrl }),
        count: own.length,
        failed: own.filter((h) => h.status === "down").length,
      },
    ];
  });
  if (sites.length === 0) return null;
  sites.sort(
    (a, b) =>
      DEPLOY_ORDER(a) - DEPLOY_ORDER(b) ||
      (b.last?.finishedAt ?? 0) - (a.last?.finishedAt ?? 0) ||
      a.target.localeCompare(b.target),
  );
  return {
    sites,
    running: sites.filter((s) => s.running).length,
    upToDate: sites.filter((s) => s.version?.branch?.upToDate).length,
    behind: sites.filter((s) => s.version?.branch?.upToDate === false).map((s) => s.target),
    failed: sites.filter((s) => s.last?.status === "down").map((s) => s.target),
    untraceable: sites.filter((s) => s.version && !s.version.commit).map((s) => s.target),
    history: [...history],
    historyFailed: history.filter((h) => h.status === "down").length,
  };
}

/** Where to deploy from, and the earlier builds kubernetes:pods says can be put back. */
function deployExtras(
  checks: readonly CheckResult[],
): Pick<DeployedVersion, "deployUrl" | "rollback"> {
  const deployUrl = checks.map((c) => webLink(c.data?.deployUrl)).find(Boolean);
  const pods = checks.find((c) => c.check === "kubernetes:pods")?.data;
  const deployment = pods?.deployment;
  const previous = pods?.previousImages;
  const [namespace, name] = typeof deployment === "string" ? deployment.split("/") : [];
  const rollback =
    namespace && name && typeof previous === "string" && previous !== ""
      ? previous.split(", ").map((image) => ({
          tag: imageTag(image),
          command: `kubectl set image -n ${namespace} deploy/${name} '*=${image}'`,
        }))
      : [];
  return { ...(deployUrl && { deployUrl }), ...(rollback.length > 0 && { rollback }) };
}

const timeOf = (value: unknown) => (typeof value === "string" ? Date.parse(value) : Number.NaN);

function backupCell(check: CheckResult | undefined, now: number): BackupCell | null {
  if (!check) return null;
  const message = check.message ?? "";
  if (check.status !== "up")
    return { status: check.status, text: STATUS_LABEL[check.status], message };
  const at = timeOf(check.data?.lastRunAt ?? check.data?.testedAt);
  const bytes = check.data?.bytes;
  const parts = [
    ...(Number.isNaN(at) ? [] : [ago(now - at)]),
    ...(typeof bytes === "number" ? [formatBytes(bytes)] : []),
  ];
  return { status: "up", text: parts.join(" · ") || "OK", message };
}

/** One row per target with backup checks: problems first, then alphabetical. */
function buildBackups(checks: readonly CheckResult[], now: number): ViewModel["backups"] {
  const byTarget = new Map<string, CheckResult[]>();
  for (const check of checks) {
    const list = byTarget.get(check.target);
    if (list) list.push(check);
    else byTarget.set(check.target, [check]);
  }
  const rows: BackupRow[] = [...byTarget.entries()]
    .map(([target, list]) => {
      // "Unknown" is a check still waiting for its first report: it only shows when nothing is known.
      const known = list.filter((c) => c.status !== "unknown");
      const status = known.length > 0 ? worstOf(known) : "unknown";
      return {
        target,
        status,
        bad: status === "down" || status === "degraded",
        backup: backupCell(
          list.find((c) => c.check === "backup"),
          now,
        ),
        restore: backupCell(
          list.find((c) => c.check === "restore"),
          now,
        ),
      };
    })
    .sort((a, b) => RANK[b.status] - RANK[a.status] || a.target.localeCompare(b.target));
  const needAttention = rows.filter((r) => r.bad).length;
  const lastRun = Math.max(
    ...checks
      .filter((c) => c.check === "backup")
      .map((c) => timeOf(c.data?.lastRunAt))
      .filter((t) => !Number.isNaN(t)),
  );
  return {
    rows,
    needAttention,
    summary:
      rows.length === 0
        ? ""
        : needAttention
          ? plural(needAttention, "needs attention", "need attention")
          : `all ${rows.length} OK${Number.isFinite(lastRun) ? ` · last ${ago(now - lastRun)}` : ""}`,
  };
}

function certDays(
  check: CheckResult,
  now: number,
): { days: number | null; expiresOn: string | null } {
  const expiresAt = check.data?.expiresAt;
  if (typeof expiresAt !== "string") return { days: null, expiresOn: null };
  const at = Date.parse(expiresAt);
  if (Number.isNaN(at)) return { days: null, expiresOn: null };
  return { days: Math.floor((at - now) / DAY_MS), expiresOn: expiresAt.slice(0, 10) };
}

function certText(days: number | null, expiresOn: string | null, message: string): string {
  if (days === null) return message;
  if (days < 0) return `expired ${expiresOn}`;
  return `expires ${expiresOn} (${plural(days, "day", "days")})`;
}

/** "99.98 %"; never rounds a real failure up to 100. */
export function formatUptime(value: number | null): string {
  if (value === null) return "—";
  if (value >= 100) return "100 %";
  return `${Math.min(99.99, Math.floor(value * 100) / 100).toFixed(2)} %`;
}

export function buildViewModel({
  checks,
  stats = [],
  open,
  resolved,
  now,
  density,
  usage,
  deploys,
  quiet = [],
  inventory = {},
}: ModelInput): ViewModel {
  const quietFor = new Map(quiet.map((q) => [q.target, q]));
  const statsFor = new Map(stats.map((s) => [`${s.target}\u0000${s.check}`, s]));
  const statOf = (c: CheckResult) => statsFor.get(`${c.target}\u0000${c.check}`);
  const main = checks.filter((c) => c.target !== HUB_TARGET);
  const hubChecks = checks.filter((c) => c.target === HUB_TARGET);
  const sites = main.filter((c) => !isBackup(c) && !isLogs(c));
  const backups = buildBackups(
    main.filter((c) => isBackup(c)),
    now,
  );

  const groups = new Map<string, CheckResult[]>();
  for (const check of sites) {
    const list = groups.get(check.target);
    if (list) list.push(check);
    else groups.set(check.target, [check]);
  }
  const compact =
    density === "compact" || (density === "auto" && groups.size >= AUTO_COMPACT_TARGETS);

  // Alphabetical and fixed: a target never moves around on the wall.
  const cards: TargetCard[] = [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, list]) => {
      const status = worstOf(list);
      const plain = list.filter((c) => !isTls(c.check));
      const tlsBad = list.find((c) => isTls(c.check) && c.status !== "up");
      const failing = list.filter((c) => c.status !== "up").map((c) => c.check);
      const tlsDays = tlsBad ? certDays(tlsBad, now) : null;
      const version = deployedVersion(list);
      const deploy = deployOf(list);
      const podsData = list.find((c) => c.check === "kubernetes:pods")?.data;
      const cpuLimit = num(podsData?.cpuLimitCores);
      const memoryLimit = num(podsData?.memoryLimitBytes);
      return {
        name,
        status,
        problem: status !== "up",
        checks: plain.map((c) => ({
          name: c.check,
          status: c.status,
          latency: c.latencyMs !== undefined ? `${Math.round(c.latencyMs)} ms` : "—",
          message: c.message ?? "",
          bad: c.status !== "up",
          spark: statOf(c)?.latency24h ?? [],
          uptime24h: statOf(c)?.uptime24h ?? null,
          uptime30d: statOf(c)?.uptime30d ?? null,
          ...(webLink(c.data?.url) && { link: webLink(c.data?.url) }),
        })),
        certificates: list
          .filter((c) => isTls(c.check))
          .map((c) => {
            const { days, expiresOn } = certDays(c, now);
            return {
              check: c.check,
              status: c.status,
              text: certText(days, expiresOn, c.message ?? ""),
            };
          }),
        backups: main
          .filter((c) => c.target === name && isBackup(c))
          .map((c) => ({ check: c.check, status: c.status, text: c.message ?? "" })),
        ...(version && { version }),
        ...(deploy && { deploy }),
        ...(quietFor.get(name) && { quiet: quietFor.get(name) }),
        sheet: buildSheet(
          [...list, ...main.filter((c) => c.target === name && isBackup(c))],
          inventory[name],
          now,
        ),
        ...((cpuLimit !== null || memoryLimit !== null) && {
          limits: {
            ...(cpuLimit !== null && { cpu: cpuLimit }),
            ...(memoryLimit !== null && { memory: memoryLimit }),
          },
        }),
        uptime30d: list.reduce<number | null>((worst, c) => {
          const u = statOf(c)?.uptime30d ?? null;
          return u === null ? worst : worst === null ? u : Math.min(worst, u);
        }, null),
        summary: (status !== "up" ? failing : plain.map((c) => c.check)).join(" · "),
        upRatio: `${list.filter((c) => c.status === "up").length}/${list.length} up`,
        ...(tlsBad &&
          tlsDays && {
            certProblem: {
              check: tlsBad.check,
              status: tlsBad.status,
              text: certText(tlsDays.days, tlsDays.expiresOn, tlsBad.message ?? ""),
            },
          }),
      };
    });

  const counts = (["up", "degraded", "down", "unknown"] as const).map((status) => ({
    status,
    n: main.filter((c) => c.status === status && !isLogs(c)).length,
  }));

  const openRows: OpenIncidentRow[] = open
    .filter((i) => !i.resolvedAt && i.target !== HUB_TARGET && !isLogs(i))
    .map((i) => ({
      id: i.id,
      target: i.target,
      check: i.check,
      status: i.status,
      message: i.message ?? "",
      openedAt: Date.parse(i.openedAt),
    }))
    .sort((a, b) => RANK[b.status] - RANK[a.status] || a.openedAt - b.openedAt);

  // By when each run finished: the hub lists them by when it first saw them, which for a
  // target added later (or a run seen late) is not when it happened.
  const deployHistory = (deploys ?? [])
    .flatMap((d) => deployRows([d.data], now).map((row) => ({ ...row, target: d.target })))
    .sort((a, b) => (b.finishedAt ?? 0) - (a.finishedAt ?? 0));

  const resolvedRows: ResolvedRow[] = resolved
    .filter((i) => i.resolvedAt && now - Date.parse(i.resolvedAt) < DAY_MS && !isLogs(i))
    .sort((a, b) => Date.parse(b.resolvedAt ?? "") - Date.parse(a.resolvedAt ?? ""))
    .map((i) => ({
      id: i.id,
      target: i.target,
      check: i.check,
      status: i.status,
      text: `was ${i.status} for ${duration(Date.parse(i.resolvedAt ?? "") - Date.parse(i.openedAt))}`,
      resolvedAt: Date.parse(i.resolvedAt ?? ""),
      message: i.message ?? "",
    }));

  const certs: CertRow[] = main
    .filter((c) => isTls(c.check))
    .map((c) => {
      const { days, expiresOn } = certDays(c, now);
      return {
        target: c.target,
        check: c.check,
        status: c.status,
        days,
        expiresOn,
        bad: c.status !== "up",
        message: c.message ?? "",
      };
    })
    .sort((a, b) => (a.days ?? 1e9) - (b.days ?? 1e9));
  const needAttention = certs.filter((c) => c.bad).length;
  const shown = certs.filter((c, i) => c.bad || i < needAttention + HEALTHY_CERTS_SHOWN);
  const hidden = certs.slice(shown.length);
  const firstHidden = hidden[0]?.days;
  const nextExpiry = certs[0]?.days;

  const down = cards.filter((c) => c.status === "down");
  const degraded = cards.filter((c) => c.status === "degraded");
  const unknown = cards.filter((c) => c.status === "unknown");
  const describe = (list: TargetCard[]) =>
    capList(
      list.map((c) => {
        const bad = (groups.get(c.name) ?? []).filter((x) => x.status !== "up").map((x) => x.check);
        return `${c.name} (${bad.join(", ")})`;
      }),
      HERO_NAMES,
    );

  let hero: Hero;
  if (main.length === 0) {
    hero = {
      tone: "empty",
      title: "No checks configured yet",
      detail: "The hub is running and reachable, but has nothing to watch.",
    };
  } else if (down.length > 0) {
    const rest = degraded.length
      ? `  ·  ${degraded.length} degraded: ${capList(
          degraded.map((c) => c.name),
          3,
        )}`
      : "";
    const longest = openRows.find((i) => i.status === "down") ?? openRows[0];
    hero = {
      tone: "down",
      title: `${plural(down.length, "target", "targets")} down`,
      detail: describe(down) + rest,
      ...(longest && { longestSince: longest.openedAt }),
    };
  } else if (degraded.length > 0) {
    hero = {
      tone: "degraded",
      title: `${plural(degraded.length, "target", "targets")} degraded`,
      detail: `${describe(degraded)} — everything else is up`,
    };
  } else if (unknown.length > 0) {
    hero = {
      tone: "unknown",
      title: `${plural(unknown.length, "target", "targets")} unknown`,
      detail: unknown.map((c) => c.name).join(", "),
    };
  } else if (backups.needAttention > 0) {
    hero = {
      tone: "degraded",
      title: "Backups need attention",
      detail: `${capList(
        backups.rows
          .filter((r) => r.bad)
          .map((r) => `${r.target} (${r.backup?.status === "up" ? "restore test" : "backup"})`),
        HERO_NAMES,
      )} — every site is up`,
    };
  } else {
    const last = resolvedRows[0];
    hero = {
      tone: "ok",
      title: "All systems operational",
      detail:
        `${plural(main.length, "check", "checks")} across ${plural(cards.length, "target", "targets")}` +
        (last ? ` · last incident resolved ${ago(now - last.resolvedAt)}` : ""),
    };
  }

  return {
    empty: main.length === 0,
    overview:
      stats.length > 0
        ? buildOverviews(
            cards,
            stats.filter((s) => !isBackup(s) && !isLogs(s)),
            now,
          )
        : null,
    compact,
    counts,
    hero,
    hub: {
      total: hubChecks.length,
      failing: hubChecks
        .filter((c) => c.status !== "up")
        .map((c) => ({
          check: c.check,
          status: c.status,
          message: c.message ?? STATUS_LABEL[c.status],
        })),
    },
    open: openRows,
    cards,
    targetsMeta: `${plural(cards.length, "target", "targets")} · ${plural(main.length, "check", "checks")}`,
    certs: {
      shown,
      more:
        hidden.length && firstHidden !== undefined && firstHidden !== null
          ? `+${hidden.length} more, all valid ≥ ${firstHidden} d`
          : hidden.length
            ? `+${hidden.length} more`
            : "",
      summary:
        certs.length === 0
          ? ""
          : needAttention
            ? plural(needAttention, "needs attention", "need attention")
            : `all ${certs.length} valid${nextExpiry !== null && nextExpiry !== undefined ? ` · next in ${nextExpiry} d` : ""}`,
      needAttention,
    },
    backups,
    resolved: resolvedRows,
    cluster: buildCluster(
      main.filter((c) => c.target === CLUSTER_TARGET),
      usage,
    ),
    deploys: cards.some((c) => c.deploy)
      ? {
          running: cards.flatMap((c) =>
            c.deploy?.running ? [{ target: c.name, ...c.deploy.running }] : [],
          ),
          recent: deployHistory.slice(0, RAIL_DEPLOYS),
        }
      : null,
    deployBoard: buildDeployBoard(cards, main, deployHistory, now),
    errorBoard: buildErrorBoard(
      main.filter((c) => c.check === LOG_CHECK),
      statOf,
    ),
    appErrors: buildAppErrors(main.filter((c) => c.check === APP_ERRORS_CHECK)),
    securityBoard: buildSecurityBoard(main.filter((c) => c.check.startsWith(SECURITY_PREFIX))),
    serverBoard: buildServerBoard(main.filter((c) => c.check.startsWith(STOCK_PREFIX))),
  };
}

const num = (value: string | number | boolean | undefined) =>
  typeof value === "number" ? value : null;

/** The cluster panel: one row per node (alphabetical by alias), then storage, certificates, pods. */
export function buildCluster(
  checks: readonly CheckResult[],
  usage?: NodeUsage,
): ClusterView | null {
  if (checks.length === 0) return null;
  const part = (check: string): ClusterPart | null => {
    const c = checks.find((x) => x.check === check);
    return c ? { status: c.status, text: c.message ?? STATUS_LABEL[c.status] } : null;
  };
  const nodes = checks
    .filter((c) => c.check.startsWith("node:"))
    .map((c): ClusterNodeRow => {
      const alias = c.check.slice("node:".length);
      const pods = num(c.data?.pods);
      const capacity = num(c.data?.podsCapacity);
      const targets = typeof c.data?.targets === "string" ? c.data.targets : "";
      return {
        alias,
        node: typeof c.data?.node === "string" ? c.data.node : alias,
        status: c.status,
        message: c.message ?? "",
        cpu: num(c.data?.cpuRatio),
        memory: num(c.data?.memoryRatio),
        pods: pods === null ? "?" : capacity === null ? `${pods}` : `${pods}/${capacity}`,
        targets: targets === "" ? [] : targets.split(", "),
        cpuSpark: usage?.cpu[alias] ?? [],
        memorySpark: usage?.memory[alias] ?? [],
      };
    })
    .sort((a, b) => a.alias.localeCompare(b.alias));
  const storage = checks.find((c) => c.check === "storage");
  return {
    status: worstOf(checks),
    nodes,
    storage: storage
      ? {
          status: storage.status,
          text: storage.message ?? STATUS_LABEL[storage.status],
          ratio: num(storage.data?.usedRatio),
        }
      : null,
    certificates: part("certificates"),
    pods: part("pods"),
  };
}

const HOUR_MS = 3_600_000;

function buildOverviews(
  cards: readonly TargetCard[],
  stats: readonly CheckStats[],
  now: number,
): Record<OverviewRange, Overview> {
  const currentHour = Math.floor(now / HOUR_MS) * HOUR_MS;
  const byTarget = new Map<string, CheckStats[]>();
  for (const s of stats) {
    if (s.target === HUB_TARGET) continue;
    const list = byTarget.get(s.target);
    if (list) list.push(s);
    else byTarget.set(s.target, [s]);
  }

  const build = (range: OverviewRange): Overview => {
    const n = range === "24h" ? 24 : 30;
    const slotMs = range === "24h" ? HOUR_MS : DAY_MS;
    // Hour slots end with the current hour; day slots are rolling 24 h blocks ending with it too.
    const lastStart = range === "24h" ? currentHour : currentHour + HOUR_MS - DAY_MS;
    const slotStarts = Array.from({ length: n }, (_, i) => lastStart - (n - 1 - i) * slotMs);
    const rows = cards.map((card) => {
      const checks = byTarget.get(card.name) ?? [];
      const slots = Array.from({ length: n }, (_, i) => {
        const counts = { up: 0, degraded: 0, down: 0, unknown: 0 };
        for (const c of checks) {
          const slot = (range === "24h" ? c.counts24h : c.counts30d)[i];
          if (!slot) continue;
          counts.up += slot.up;
          counts.degraded += slot.degraded;
          counts.down += slot.down;
          counts.unknown += slot.unknown;
        }
        return { status: pointStatus(counts), counts };
      });
      const uptimes = checks
        .map((c) => (range === "24h" ? c.uptime24h : c.uptime30d))
        .filter((u): u is number => u !== null);
      return {
        name: card.name,
        status: card.status,
        uptime: uptimes.length > 0 ? Math.min(...uptimes) : null,
        slots,
        failingSlots: slots.filter((s) => s.counts.down > 0).length,
      };
    });
    return { range, slotStarts, slotMs, rows };
  };

  return { "24h": build("24h"), "30d": build("30d") };
}

/* ---------------- problems in a window ---------------- */

/** What the reader asked about: one check, between two instants. */
export interface ProblemWindow {
  check: string;
  from: number;
  to: number;
  /** "02:10–02:15", or the incident it came from. */
  label: string;
}

const PHASES: [string, string][] = [
  ["dnsMs", "DNS"],
  ["connectMs", "TCP"],
  ["tlsMs", "TLS"],
  ["waitMs", "server"],
];

/** "DNS 3 ms · TCP 18 ms · TLS 45 ms" from an HTTP result's stage timings. */
export function phaseText(data: CheckResult["data"]): string | null {
  const parts = PHASES.flatMap(([key, label]) =>
    typeof data?.[key] === "number" ? [`${label} ${data[key]} ms`] : [],
  );
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** "1 of 5 checks failed" / "No failures in this period". */
export function problemHeadline(failed: number, total: number, truncated: boolean): string {
  if (failed === 0)
    return total > 0 ? `No failures among ${total} checks` : "No results in this period";
  return `${truncated ? "More than " : ""}${failed} of ${total} checks failed`;
}

export const LEVEL_LABEL: Record<HubUser["level"], string> = {
  read: "View",
  deploy: "Deploy",
  admin: "Admin",
};

export interface UserRow {
  email: string;
  level: HubUser["level"];
  levelLabel: string;
  /** Also has the Cluster and Security views (of their sites). */
  infra: boolean;
  /** "All sites", or the list. */
  sites: string;
  invited: string;
  /** "Never signed in" until they come. */
  lastSeen: string;
  /** Not signed in since being invited. */
  pending: boolean;
}

/** The users view's rows: admins first, then by email. */
export function userRows(users: readonly HubUser[], now: number): UserRow[] {
  const rank = { admin: 0, deploy: 1, read: 2 };
  return [...users]
    .sort((a, b) => rank[a.level] - rank[b.level] || a.email.localeCompare(b.email))
    .map((u) => {
      const seen = u.lastSeenAt ? Date.parse(u.lastSeenAt) : Number.NaN;
      return {
        email: u.email,
        level: u.level,
        levelLabel: LEVEL_LABEL[u.level],
        infra: u.infra && u.level !== "admin",
        sites: u.sites === null ? "All sites" : u.sites.join(", "),
        invited: `by ${u.invitedBy} · ${ago(now - Date.parse(u.invitedAt))}`,
        lastSeen: Number.isNaN(seen) ? "Never signed in" : `Seen ${ago(now - seen)}`,
        pending: Number.isNaN(seen),
      };
    });
}

export interface VolumeRow {
  target: string;
  /** The claim's name. */
  pvc: string;
  status: CheckStatus;
  /** 0–1, null when unknown. */
  ratio: number | null;
  message: string;
}

/** Every volume of every target (`volume:<claim>` checks), fullest first. */
export function volumeRows(checks: readonly CheckResult[]): VolumeRow[] {
  return checks
    .filter((c) => c.check.startsWith("volume:"))
    .map((c) => ({
      target: c.target,
      pvc: c.check.slice("volume:".length),
      status: c.status,
      ratio: typeof c.data?.usedRatio === "number" ? c.data.usedRatio : null,
      message: c.message ?? "",
    }))
    .sort((a, b) => (b.ratio ?? -1) - (a.ratio ?? -1) || a.target.localeCompare(b.target));
}

export interface UsageChart {
  label: string;
  /** Average per bucket, oldest first; null where nothing was measured. */
  values: (number | null)[];
  /** The pods' limit, when they have one: the chart's top. */
  limit: number | null;
  now: string;
  peak: string;
  limitText: string | null;
  /** Latest value as a share of the limit, 0–1. */
  ratio: number | null;
}

/** "250m" below a core, "1.50 cores" from one: how Kubernetes writes CPU. */
export const fmtCores = (cores: number) =>
  cores < 1 ? `${Math.round(cores * 1000)}m` : `${cores.toFixed(2)} cores`;

/** "350 MiB", "1.2 GiB": binary, like the pods' limits. */
export const fmtMemory = (bytes: number) =>
  bytes >= 2 ** 30 ? `${(bytes / 2 ** 30).toFixed(1)} GiB` : `${Math.round(bytes / 2 ** 20)} MiB`;

/** One target's series of a usage metric, as a chart against its pods' limit. */
export function usageChart(
  label: string,
  series: {
    labels: Record<string, string>;
    points: { avg: number | null; max: number | null }[];
  }[],
  target: string,
  limit: number | undefined,
  format: (v: number) => string,
): UsageChart | null {
  const own = series.find((s) => s.labels.target === target);
  if (!own) return null;
  const values = own.points.map((p) => p.avg);
  const present = values.filter((v): v is number => v !== null);
  if (present.length === 0) return null;
  const peak = Math.max(...own.points.map((p) => p.max ?? p.avg ?? 0));
  const latest = present[present.length - 1] as number;
  return {
    label,
    values,
    limit: limit ?? null,
    now: format(latest),
    peak: format(peak),
    limitText: limit ? format(limit) : null,
    ratio: limit ? latest / limit : null,
  };
}

const str = (v: unknown) => (typeof v === "string" ? v : undefined);

/** What the hub knows about a site, from its checks, plus what the config says. */
export function buildSheet(
  checks: readonly CheckResult[],
  inventory: SiteInventory | undefined,
  now: number,
): SiteSheet {
  const addresses = [
    ...new Set(
      checks.flatMap((c) => {
        const url = str(c.data?.url);
        if (!c.check.startsWith("http:") || !url) return [];
        try {
          return [new URL(url).origin];
        } catch {
          return [];
        }
      }),
    ),
  ].sort();
  const ci = checks.find((c) => c.data?.deployUrl) ?? checks.find((c) => c.check.startsWith("ci:"));
  const repoName = str(ci?.data?.repo);
  const runUrl = str(ci?.data?.url);
  const pods = checks.find((c) => c.check === "kubernetes:pods")?.data;
  const image = str(pods?.image);
  const backup = checks.find((c) => c.check === "backup")?.data;
  const backupAt = timeOf(backup?.lastRunAt);
  const certs = checks
    .map((c) => timeOf(c.check.startsWith("tls:") ? c.data?.expiresAt : undefined))
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => a - b);
  const deployment = str(pods?.deployment);
  return {
    addresses,
    ...(repoName && {
      repo: {
        name: repoName,
        ...(runUrl && { url: runUrl.split("/actions/")[0] }),
        ...(str(ci?.data?.branch) && { branch: str(ci?.data?.branch) }),
      },
    }),
    ...(deployment && { deployment }),
    ...(image && { running: [...new Set(image.split(", ").map(imageTag))].join(", ") }),
    volumes: checks
      .filter((c) => c.check.startsWith("volume:"))
      .map((c) => ({
        name: c.check.slice("volume:".length),
        size: typeof c.data?.capacityBytes === "number" ? fmtMemory(c.data.capacityBytes) : "—",
      })),
    ...(!Number.isNaN(backupAt) && {
      backup: {
        when: ago(now - backupAt),
        size: typeof backup?.bytes === "number" ? formatBytes(backup.bytes) : "—",
      },
    }),
    ...(certs[0] !== undefined && {
      certificate: `expires ${new Date(certs[0]).toISOString().slice(0, 10)}`,
    }),
    ...(inventory && { inventory }),
  };
}

export interface PaletteItem {
  key: string;
  label: string;
  /** What else matched, or where it leads. */
  hint: string;
  href: string;
  status?: CheckStatus;
}

const fold = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/**
 * The quick search: views and sites matching `query` (accents and case ignored), best first —
 * a site whose name starts with it, then contains it, then one whose address, client or
 * contact does. Empty query: the views, then the sites with problems first.
 */
export function paletteItems(
  cards: readonly TargetCard[],
  views: readonly { id: string; label: string; href: string }[],
  query: string,
  limit = 12,
): PaletteItem[] {
  const q = fold(query.trim());
  const viewItems = views.map((v) => ({
    key: `view:${v.id}`,
    label: v.label,
    hint: "view",
    href: v.href,
  }));
  const siteItems = cards.map((c) => {
    const inv = c.sheet.inventory;
    const extra = [
      inv?.client,
      ...c.sheet.addresses.map((a) => a.replace(/^https?:\/\//, "")),
      inv?.contact,
    ].filter((x): x is string => Boolean(x));
    return { card: c, extra };
  });
  if (q === "") {
    return [
      ...viewItems,
      ...[...siteItems]
        .sort(
          (a, b) =>
            RANK[b.card.status] - RANK[a.card.status] || a.card.name.localeCompare(b.card.name),
        )
        .map(({ card, extra }) => siteItem(card, extra[0] ?? card.summary)),
    ].slice(0, limit);
  }
  const scored: { score: number; item: PaletteItem }[] = [];
  for (const v of viewItems) {
    const name = fold(v.label);
    if (name.startsWith(q)) scored.push({ score: 0, item: v });
    else if (name.includes(q)) scored.push({ score: 2, item: v });
  }
  for (const { card, extra } of siteItems) {
    const name = fold(card.name);
    const hit = extra.find((x) => fold(x).includes(q));
    if (name.startsWith(q)) scored.push({ score: 1, item: siteItem(card, extra[0] ?? "") });
    else if (name.includes(q)) scored.push({ score: 2, item: siteItem(card, extra[0] ?? "") });
    else if (hit) scored.push({ score: 3, item: siteItem(card, hit) });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.item.label.localeCompare(b.item.label))
    .slice(0, limit)
    .map((s) => s.item);
}

function siteItem(card: TargetCard, hint: string): PaletteItem {
  return {
    key: `site:${card.name}`,
    label: card.name,
    hint,
    href: `#/target/${encodeURIComponent(card.name)}`,
    status: card.status,
  };
}

/** "2026-10", "2026-09"… the `count` months up to the one `now` is in (local time). */
export function recentMonths(now: number, count: number): string[] {
  const d = new Date(now);
  return Array.from({ length: count }, (_, i) => {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    return `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`;
  });
}

/** "October 2026". */
export const monthLabel = (month: string) => {
  const [y, m] = month.split("-").map(Number);
  return new Date(y ?? 2000, (m ?? 1) - 1, 1).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
};

export const reportHref = (target: string, month: string) =>
  `#/report/${encodeURIComponent(target)}/${month}`;

/** `#/report/<target>/<month>` → its parts. */
export function readReportHash(hash: string): { target: string; month: string } | null {
  const match = /^#\/report\/([^/]+)\/(\d{4}-\d{2})$/.exec(hash);
  if (!match?.[1] || !match[2]) return null;
  try {
    return { target: decodeURIComponent(match[1]), month: match[2] };
  } catch {
    return null;
  }
}

export interface ReportView {
  title: string;
  availability: string;
  /** "up" from 99.5 %, "degraded" from 95 %, "down" below: the wall's colors. */
  tone: CheckStatus;
  /** "Measured 9 of 31 days: the hub started watching on 23 Oct." when partial. */
  coverage: string | null;
  checks: { check: string; availability: string; down: string }[];
  interruptions: {
    when: string;
    duration: string;
    checks: string;
    cause: string;
    planned: boolean;
  }[];
  unplannedMinutes: number;
  errors: string;
  backups: string | null;
  maintenance: { when: string; reason: string }[];
}

const minutesText = (m: number) =>
  m < 60 ? `${m} min` : m % 60 === 0 ? `${m / 60} h` : `${Math.floor(m / 60)} h ${m % 60} min`;

/** What the monthly report page shows. */
export function reportView(r: MonthlyReportResponse, fmtTime: (iso: string) => string): ReportView {
  const pct = r.availability;
  const unplanned = r.interruptions.filter((i) => !i.planned);
  return {
    title: `${r.target} · ${monthLabel(r.month)}`,
    availability: formatUptime(pct),
    tone: pct === null ? "unknown" : pct >= 99.5 ? "up" : pct >= 95 ? "degraded" : "down",
    coverage:
      r.measuredHours < r.totalHours - 1
        ? `Measured ${Math.round(r.measuredHours / 24)} of ${Math.round(r.totalHours / 24)} days so far`
        : null,
    checks: r.checks
      .filter((c) => c.availability !== null)
      .map((c) => ({
        check: c.check,
        availability: formatUptime(c.availability),
        down: c.downMinutes > 0 ? minutesText(c.downMinutes) : "—",
      })),
    interruptions: r.interruptions.map((i) => ({
      when: `${fmtTime(i.from)} – ${i.ongoing ? "ongoing" : fmtTime(i.to)}`,
      duration: minutesText(i.minutes),
      checks: i.checks.join(", "),
      cause: i.cause,
      planned: i.planned,
    })),
    unplannedMinutes: unplanned.reduce((n, i) => n + i.minutes, 0),
    errors:
      r.errorHours === 0
        ? "No hour with serious errors in its logs"
        : `${plural(r.errorHours, "hour", "hours")} with serious errors in its logs`,
    backups:
      r.backupCoverage === null
        ? null
        : `Up to date ${formatUptime(r.backupCoverage)} of the month`,
    maintenance: r.maintenance.map((m) => ({
      when: `${fmtTime(m.from)} – ${fmtTime(m.to)}`,
      reason: m.reason,
    })),
  };
}

export interface SecurityFinding {
  origin: string;
  code: string;
  severity: "critical" | "high" | "medium" | "low" | "info";
  evidence: string;
}

export interface SecurityVuln {
  id: string;
  package: string;
  version: string;
  severity: "CRITICAL" | "HIGH" | "MODERATE" | "LOW" | "UNKNOWN";
  summary: string;
  fixed?: string;
  file: string;
}

export interface SecurityRuntime {
  image: string;
  product: string;
  cycle: string;
  state: "eol" | "soon";
  eol: string;
  file: string;
}

export interface SecuritySite {
  target: string;
  /** The worst of its two checks. */
  status: CheckStatus;
  web?: {
    grade: string;
    score: number;
    message: string;
    findings: SecurityFinding[];
    status: CheckStatus;
  };
  deps?: {
    message: string;
    status: CheckStatus;
    counts: { critical: number; high: number; moderate: number; low: number };
    vulnerabilities: SecurityVuln[];
    /** Beyond the ones listed. */
    more: number;
    runtimes: SecurityRuntime[];
  };
}

export interface SecurityBoard {
  /** Worst first: critical findings, then vulnerabilities, then grade. */
  sites: SecuritySite[];
  /** Sites graded D or F on the web scan. */
  poorGrades: string[];
  critical: number;
  high: number;
  /** Runtimes out of support, across sites ("mysql 5.6"). */
  outOfSupport: string[];
  unscanned: string[];
}

const parseJson = <T>(value: unknown, fallback: T): T => {
  if (typeof value !== "string") return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

/** The security view, from `security:web` and `security:dependencies`. */
export function buildSecurityBoard(checks: readonly CheckResult[]): SecurityBoard | null {
  if (checks.length === 0) return null;
  const targets = [...new Set(checks.map((c) => c.target))];
  const n = (v: unknown) => (typeof v === "number" ? v : 0);
  const sites = targets.map((target): SecuritySite => {
    const web = checks.find((c) => c.target === target && c.check === "security:web");
    const deps = checks.find((c) => c.target === target && c.check === "security:dependencies");
    const details = parseJson<{
      vulnerabilities?: SecurityVuln[];
      runtimes?: SecurityRuntime[];
      more?: number;
    }>(deps?.data?.details, {});
    return {
      target,
      status: worstOf([web, deps].filter((c): c is CheckResult => c !== undefined)),
      ...(web && {
        web: {
          grade: typeof web.data?.grade === "string" ? web.data.grade : "?",
          score: n(web.data?.score),
          message: web.message ?? "",
          findings: parseJson<SecurityFinding[]>(web.data?.details, []),
          status: web.status,
        },
      }),
      ...(deps && {
        deps: {
          message: deps.message ?? "",
          status: deps.status,
          counts: {
            critical: n(deps.data?.critical),
            high: n(deps.data?.high),
            moderate: n(deps.data?.moderate),
            low: n(deps.data?.low),
          },
          vulnerabilities: details.vulnerabilities ?? [],
          more: details.more ?? 0,
          runtimes: details.runtimes ?? [],
        },
      }),
    };
  });
  const weight = (s: SecuritySite) =>
    (s.web?.findings.some((f) => f.severity === "critical") ? 1000 : 0) +
    (s.deps?.counts.critical ?? 0) * 100 +
    (s.deps?.counts.high ?? 0) * 10 +
    (s.deps?.runtimes.filter((r) => r.state === "eol").length ?? 0) * 10 +
    (100 - (s.web?.score ?? 100));
  sites.sort((a, b) => weight(b) - weight(a) || a.target.localeCompare(b.target));
  return {
    sites,
    poorGrades: sites.filter((s) => s.web && ["D", "F"].includes(s.web.grade)).map((s) => s.target),
    critical: sites.reduce((t, s) => t + (s.deps?.counts.critical ?? 0), 0),
    high: sites.reduce((t, s) => t + (s.deps?.counts.high ?? 0), 0),
    outOfSupport: [
      ...new Set(
        sites.flatMap((s) =>
          (s.deps?.runtimes ?? [])
            .filter((r) => r.state === "eol")
            .map((r) => `${r.product} ${r.cycle}`),
        ),
      ),
    ].sort(),
    unscanned: sites.filter((s) => s.status === "unknown").map((s) => s.target),
  };
}

/** OVHcloud datacenter codes, for people. Unknown codes are shown as they are. */
const DATACENTER_NAMES: Record<string, string> = {
  bhs: "Beauharnois, CA",
  gra: "Gravelines, FR",
  rbx: "Roubaix, FR",
  sbg: "Strasbourg, FR",
  fra: "Frankfurt, DE",
  lon: "London, UK",
  waw: "Warsaw, PL",
  hil: "Hillsboro, US",
  vin: "Vint Hill, US",
  sgp: "Singapore",
  syd: "Sydney, AU",
  ynm: "Mumbai, IN",
};

export const datacenterName = (code: string) => DATACENTER_NAMES[code] ?? code;

export type StockState = "available" | "out" | "unknown";

export interface ServerDatacenter {
  code: string;
  available: boolean;
  /** Delivery estimate when available ("1H-low", "72H"). */
  estimate?: string;
}

export interface ServerRow {
  target: string;
  check: string;
  /** The watch's name, the part of the check after `stock:`. */
  watch: string;
  label: string;
  planCode: string;
  state: StockState;
  /** Status color: green when it can be ordered, grey otherwise. */
  tone: CheckStatus;
  /** Watched datacenters, available first. */
  datacenters: ServerDatacenter[];
  /** Since when (epoch ms) it has been in its current state, as far as the hub has seen. */
  since: number | null;
  checkedAt: number;
  message: string;
  orderUrl?: string;
}

export interface ServerBoard {
  rows: ServerRow[];
  /** How many watches can be ordered now. */
  available: number;
}

const csv = (value: unknown) => (typeof value === "string" && value !== "" ? value.split(",") : []);

/** The Servers view, from `stock:*` checks: what can be ordered now first, then by name. */
export function buildServerBoard(checks: readonly CheckResult[]): ServerBoard | null {
  if (checks.length === 0) return null;
  const rows = checks.map((c): ServerRow => {
    const data = c.data ?? {};
    const open = new Set(csv(data.datacenters));
    const estimates = new Map(
      csv(data.estimates).map((pair) => {
        const at = pair.indexOf(":");
        return [pair.slice(0, at), pair.slice(at + 1)] as const;
      }),
    );
    const codes = [...new Set([...csv(data.watched), ...open])];
    const state: StockState =
      c.status === "unknown" ? "unknown" : data.available === true ? "available" : "out";
    const orderUrl = webLink(data.orderUrl);
    return {
      target: c.target,
      check: c.check,
      watch: c.check.slice(STOCK_PREFIX.length),
      label: typeof data.label === "string" ? data.label : c.check.slice(STOCK_PREFIX.length),
      planCode: typeof data.planCode === "string" ? data.planCode : "",
      state,
      tone: state === "available" ? "up" : "unknown",
      datacenters: codes
        .map((code) => ({
          code,
          available: state === "available" && open.has(code),
          ...(estimates.has(code) && { estimate: estimates.get(code) }),
        }))
        .sort((a, b) => Number(b.available) - Number(a.available) || a.code.localeCompare(b.code)),
      since: typeof data.since === "number" ? data.since : null,
      checkedAt: Date.parse(c.observedAt),
      message: c.message ?? "",
      ...(orderUrl && { orderUrl }),
    };
  });
  const rank: Record<StockState, number> = { available: 0, out: 1, unknown: 2 };
  rows.sort((a, b) => rank[a.state] - rank[b.state] || a.label.localeCompare(b.label));
  return { rows, available: rows.filter((r) => r.state === "available").length };
}

/**
 * One watched datacenter's last week, from the `ovh_server_available` series: per hour, whether
 * it could be ordered at any point ("up"), not at all ("unknown"), or wasn't checked ("none").
 */
export function stockHistory(
  series: MetricSeriesResponse,
  watch: string,
  datacenter: string,
): { t: number; status: "up" | "unknown" | "none" }[] {
  const match = series.series.find(
    (s) => s.labels.watch === watch && s.labels.datacenter === datacenter,
  );
  const points = match?.points ?? [];
  if (points.length === 0) {
    const from = Date.parse(series.from);
    const to = Date.parse(series.to);
    const slots: { t: number; status: "none" }[] = [];
    for (let t = from; t < to; t += series.bucketMs) slots.push({ t, status: "none" });
    return slots;
  }
  return points.map((p) => ({
    t: Date.parse(p.t),
    status: p.max === null ? "none" : p.max > 0 ? "up" : "unknown",
  }));
}
